import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MenuTreeEditor from './components/MenuTreeEditor';
import DeleteNodeModal from './components/DeleteNodeModal';
import { menuService } from '../../services/menuService';
import { useAuth } from '../../contexts/AuthContext';

const MenuSettings = () => {
  const { t } = useTranslation();
  const { userProfile, activeCompanyId } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const companyId = activeCompanyId || userProfile?.company_id;

  const loadTree = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await menuService.getMenuTree(companyId);
      setTree(data);
    } catch (err) {
      setError(err?.message || t('menuSettings.loadError'));
    } finally {
      setLoading(false);
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTree(); }, [loadTree]);

  // Count siblings at root level for new order value
  const countAtLevel = (nodes) => nodes.length;

  const handleAddRoot = async () => {
    try {
      const order = countAtLevel(tree);
      const node = await menuService.createNode({
        companyId,
        parentId: null,
        name: t('menuSettings.newCategory'),
        order,
      });
      setTree((prev) => [...prev, { ...node, children: [] }]);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAddChild = async (parentId) => {
    const findNode = (nodes) => {
      for (const n of nodes) {
        if (n.id === parentId) return n;
        const found = findNode(n.children || []);
        if (found) return found;
      }
      return null;
    };
    const parent = findNode(tree);
    const order = (parent?.children?.length) || 0;
    try {
      const node = await menuService.createNode({
        companyId,
        parentId,
        name: t('menuSettings.newCategory'),
        order,
      });
      const addChild = (nodes) =>
        nodes.map((n) => {
          if (n.id === parentId) return { ...n, children: [...(n.children || []), { ...node, children: [] }] };
          return { ...n, children: addChild(n.children || []) };
        });
      setTree((prev) => addChild(prev));
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleRename = async (nodeId, name) => {
    setSavingIds((prev) => new Set(prev).add(nodeId));
    try {
      await menuService.updateNode(nodeId, { name });
      const rename = (nodes) =>
        nodes.map((n) => {
          if (n.id === nodeId) return { ...n, name };
          return { ...n, children: rename(n.children || []) };
        });
      setTree((prev) => rename(prev));
    } catch (err) {
      setError(err?.message);
    } finally {
      setSavingIds((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    }
  };

  const swapOrder = async (nodes, id, direction) => {
    const idx = nodes.findIndex((n) => n.id === id);
    if (idx < 0) return nodes;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= nodes.length) return nodes;
    const updated = [...nodes];
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    const reordered = updated.map((n, i) => ({ ...n, order: i }));
    await menuService.reorderNodes(reordered.map(({ id, order }) => ({ id, order })));
    return reordered;
  };

  const applySwap = async (nodeId, direction) => {
    const doSwap = async (nodes) => {
      // Try at this level
      const idx = nodes.findIndex((n) => n.id === nodeId);
      if (idx >= 0) return await swapOrder(nodes, nodeId, direction);
      // Recurse
      return await Promise.all(
        nodes.map(async (n) => ({ ...n, children: await doSwap(n.children || []) }))
      );
    };
    try {
      setTree(await doSwap(tree));
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleMoveUp = (nodeId) => applySwap(nodeId, 'up');
  const handleMoveDown = (nodeId) => applySwap(nodeId, 'down');

  const handleDeleteConfirm = async (nodeId) => {
    setIsDeleting(true);
    try {
      await menuService.deleteNode(nodeId);
      const remove = (nodes) =>
        nodes
          .filter((n) => n.id !== nodeId)
          .map((n) => ({ ...n, children: remove(n.children || []) }));
      setTree((prev) => remove(prev));
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
              {t('menuSettings.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('menuSettings.subtitle')}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
              <button className="ml-auto" onClick={() => setError(null)}>
                <Icon name="X" size={14} className="text-destructive" />
              </button>
            </div>
          )}

          {/* Card */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
            {/* Card toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-medium text-foreground">
                {t('menuSettings.treeLabel')}
              </span>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddRoot}
                disabled={loading || !companyId}
              >
                {t('menuSettings.addRoot')}
              </Button>
            </div>

            {/* Tree content */}
            <div className="p-2 min-h-[200px]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={24} className="text-muted-foreground animate-spin" />
                </div>
              ) : (
                <MenuTreeEditor
                  tree={tree}
                  companyId={companyId}
                  savingIds={savingIds}
                  onAdd={handleAddChild}
                  onRename={handleRename}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  onDelete={setDeleteTarget}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteNodeModal
        isOpen={!!deleteTarget}
        node={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default MenuSettings;
